import type { ReactNode } from 'react'

import { Chip, type TChipProps } from '@/src/elements/chip'
import type { TChipVariantProps } from '@/src/elements/chip/chip-variants'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TFullCommunityApi, TMediumCommunityApi } from '../../../api/communityApi.types'
import { COMMUNITY_ACTIVITY, COMMUNITY_STATUS } from '../../../models/community.constants'
import type { TCommunityFrequencyProps } from '../../hooks/useCommunityFrequencyText'
import { CommunityDatesChip, type TCommunityDatesChipProps } from './CommunityDatesChip'
import { CommunityFrequencyChip } from './CommunityFrequencyChip'

const inactiveStyle: Partial<TChipProps> = { colors: 'default', variant: 'flat' }

export type TCommunityActivityChipProps = Pick<TCommunityDatesChipProps, 'subscription'> &
  Pick<TCommunityFrequencyProps, 'short'> &
  Pick<TChipVariantProps, 'colors'> &
  Pick<TChipProps, 'fontWeight'> & {
    community: Pick<
      TMediumCommunityApi,
      'activity' | 'fromDate' | 'toDate' | 'publicationFrequencyType' | 'publicationFrequencyValue'
    > &
      /** CommunityActivityChip must be usable with TMediumCommunityApi => Partial */
      Partial<Pick<TFullCommunityApi, 'status'>>
  }

export const CommunityActivityChip = ({
  colors,
  community,
  fontWeight,
  short,
  subscription,
}: TCommunityActivityChipProps): ReactNode => {
  const { t } = useTranslate()

  if (community.status === COMMUNITY_STATUS.CHECK) {
    return <Chip text={t('community.status.check.validating')} iconName={ICON.HOURGLASS_HALF} {...inactiveStyle} />
  }

  if (community.status === COMMUNITY_STATUS.DRAFT) {
    return <Chip text={t('community.status.draft.word')} iconName={ICON.EDIT} {...inactiveStyle} />
  }

  if (community.activity === COMMUNITY_ACTIVITY.INACTIVE) {
    return <Chip text={t('community.activity.inactive')} {...inactiveStyle} />
  }

  if (!community.fromDate || !community.toDate) {
    return (
      <CommunityFrequencyChip
        colors={colors}
        publicationFrequencyType={community.publicationFrequencyType}
        publicationFrequencyValue={community.publicationFrequencyValue}
        short={short}
        fontWeight={fontWeight}
      />
    )
  }

  return <CommunityDatesChip short={short} community={community} subscription={subscription} />
}
